import React, { useContext, useMemo, useState } from 'react';
import { Menu } from 'antd';
import {
  DashboardOutlined,
  AppstoreOutlined,
  AuditOutlined,
  UsergroupAddOutlined,
  SettingOutlined,
  IdcardOutlined,
  LogoutOutlined,
  MobileOutlined,
  PushpinOutlined,
  FileTextOutlined,
  WifiOutlined,
  TableOutlined,
  SortAscendingOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import './BasicLayout.less';
import UserContext from './UserContext';
import checkPermission from '../../components/Authorization/Util';
import checkFormTypePermission from '../../components/Authorization/FormTypePermission';
import checkEnabledFeatures, { FEATURES } from '../../components/Authorization/Feature';
import { useHistory } from 'react-router';
import { LOGOUT } from '../../reducers/types.js';
import { fetchData } from '../../nextgen/service/dataServices.js';
import { useTranslation } from 'react-i18next';

export default function SiderMenu() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  let currentUser = useContext(UserContext);
  const dispatch = useDispatch()

  const [formList, setFormList] = useState([]);
  const isCustomFormsEnabled = useMemo(() => checkEnabledFeatures(currentUser, FEATURES.CUSTOM_FORMS), [currentUser]);
  const isSettingsEnabled = useMemo(() => checkEnabledFeatures(currentUser, FEATURES.SETTINGS), [currentUser]);
  fetchData('/api/form-schema', data => setFormList(data), undefined, isCustomFormsEnabled);

  const retrieveSelectedKeyFromPath = () => {
    const pathName = window.location.pathname.replace(/\//g, '');
    return [pathName];
  };


  const handleSiderMenuClick = action => {
    if (action.key === 'logout') {
      logout();
    } else {
      history.push('/' + action.key);
    }
  };

  const logout = () => {
    localStorage.removeItem('userToken');
    dispatch({
      type: LOGOUT,
    });
  };

  const isItemCatalogingEnabled = useMemo(() => checkEnabledFeatures(currentUser, 'Item-Cataloging'), [currentUser]);
  const isAppCmsEnabled = useMemo(() => checkEnabledFeatures(currentUser, 'App-Cms'), [currentUser]);
  const isKontaktSyncEnabled = useMemo(() => checkEnabledFeatures(currentUser, 'Kontakt-Sync'), [currentUser]);
  const hasItemReadPerm = useMemo(() => checkPermission(currentUser, 'item', 'read'), [currentUser]);
  const hasItemWritePerm = useMemo(() => checkPermission(currentUser, 'item', 'write'), [currentUser]);
  const hasFormSchemaReadPerm = useMemo(() => checkPermission(currentUser, 'formSchema', 'read'), [currentUser]);
  const hasFormSchemaWritePerm = useMemo(() => checkPermission(currentUser, 'formSchema', 'write'), [currentUser]);
  const hasAppCmsReadPerm = useMemo(() => checkPermission(currentUser, 'appcms', 'read'), [currentUser]);
  const hasAppCmsWritePerm = useMemo(() => checkPermission(currentUser, 'appcms', 'write'), [currentUser]);
  const hasUserReadPerm = useMemo(() => checkPermission(currentUser, 'user', 'read'), [currentUser]);
  const hasUserWritePerm = useMemo(() => checkPermission(currentUser, 'user', 'write'), [currentUser]);
  const hasSettingsReadPerm = useMemo(() => checkPermission(currentUser, 'setting', 'read'), [currentUser]);

  const formMenuItems = useMemo(() => {
    const allowedForms = formList.filter(fl => checkFormTypePermission(currentUser, fl.id, 'read'));

    const groupedForms = allowedForms.reduce((acc, fl) => {
      // Determine the group name. Fallback to "Ungrouped" if no group is assigned.
      const groupName = fl.groupName || 'Ungrouped';

      if (!acc[groupName]) {
        acc[groupName] = [];
      }
      
      const hasWrite = checkFormTypePermission(currentUser, fl.id, 'write');
      const formItem = {
        key: fl.id,
        label: fl.name?.[i18n.language] || fl.name?.['en'],
        children: [
          hasWrite && {
            key: `custom-form-add/${fl.id}`,
            label: t('sider.add'),
          },
          {
            key: `custom-form-show/${fl.id}`,
            label: t('sider.show'),
          }
        ].filter(Boolean),
      };

      acc[groupName].push(formItem);
      return acc;
    }, {});

    return Object.keys(groupedForms).map(groupKey => ({
      key: `group-${groupKey}`,
      label: groupKey, 
      children: groupedForms[groupKey],
    }));
  }, [formList, currentUser, i18n.language, t]);

  const items = [
    isItemCatalogingEnabled && {
      key: 'dashboard',
      label: t('sider.dashboard'),
      icon: <DashboardOutlined />,
    },
    isItemCatalogingEnabled && hasItemReadPerm && {
      key: 'object',
      label: t('sider.objects'),
      icon: <AppstoreOutlined />,
      children: [
        {
          key: 'objects',
          label: t('sider.showObjects'),
        },
        hasItemWritePerm && {
          key: 'add-object',
          label: t('sider.addObject'),
        },
      ],
    },
    isCustomFormsEnabled && formMenuItems.length > 0 && {
      key: 'forms',
      label: t('sider.forms'),
      icon: <TableOutlined />,
      children: formMenuItems,
    },
    isCustomFormsEnabled && hasFormSchemaReadPerm && {
      key: 'forms-settings',
      label: t('sider.formSettings'),
      icon: <AuditOutlined />,
      children: [
        hasFormSchemaWritePerm && {
          key: 'add-new-form',
          label: t('sider.addNewForm'),
        },
        {
          key: 'show-forms',
          label: t('sider.showForms'),
        },
      ].filter(Boolean),
    },

    isAppCmsEnabled && hasAppCmsReadPerm && {
      key: 'appCms',
      label: t('sider.appCms'),
      icon: <MobileOutlined />,
      children: [
        {
          key: 'pointsOfInterest',
          label: t('sider.poi'),
          icon: <PushpinOutlined />,
          children: [
            { key: 'pois', label: t('sider.showPois') },
            { key: 'add-poi', label: t('sider.addPoi') },
            { key: 'poi-groups', label: t('sider.poiGroups') },
            { key: 'add-poi-group', label: t('sider.addPoiGroup') },
          ],
        },
        {
          key: 'beacon-menu',
          label: t('sider.beacons'),
          icon: <WifiOutlined />,
          children: [
            { key: 'beacons', label: t('sider.showBeacons') },
            { key: 'beacon-scans', label: t('sider.showBeaconScans') },
            isKontaktSyncEnabled && { key: 'sync-logs', label: t('sider.syncLogs') },
          ].filter(Boolean),
        },
        {
          key: 'news-menu',
          label: t('sider.news'),
          icon: <FileTextOutlined />,
          children: [
            { key: 'news', label: t('sider.showNews') },
            { key: 'add-news', label: t('sider.addNews') },
          ],
        },
        {
          key: 'app-settings',
          label: t('sider.appSettings'),
          icon: <SettingOutlined />,
        },
        {
          key: 'languages',
          label: t('sider.languages'),
          icon: <SortAscendingOutlined />,
        },
      ],
    },
    hasUserReadPerm && {
      key: 'userManagement',
      label: t('sider.userManagement'),
      icon: <UsergroupAddOutlined />,
      children: [
        { key: 'user-list', label: t('sider.userList') },
        hasUserWritePerm && { key: 'add-user', label: t('sider.addUser') },
        { key: 'user-groups', label: t('sider.userGroups') },
        hasUserWritePerm && { key: 'activity-log', label: t('sider.activityLog') },
        hasUserWritePerm && { key: 'permissions', label: t('sider.permissions') },
      ]
    },
    hasSettingsReadPerm && {
      key: 'dropdowns',
      label: t('sider.dropdowns'),
      icon: <SettingOutlined />,
    },
    isSettingsEnabled && hasSettingsReadPerm && {
      key: 'settings',
      label: t('sider.settings'),
      icon: <SettingOutlined />,
    },
    {
      key: 'profile',
      label: t('sider.profile'),
      icon: <IdcardOutlined />,
    },
    {
      key: 'logout',
      label: t('sider.logout'),
      icon: <LogoutOutlined />,
    }

  ];
  return (
    <Menu
      onClick={handleSiderMenuClick}
      defaultSelectedKeys={retrieveSelectedKeyFromPath()}
      mode="inline"
      items={items}
    />
  )
}
